<template>
  <section class="p-1">
    <header class="pb-1 d-flex justify-content-between align-items-start">
      <div class="d-flex column align-items-center">
        <span class="brand-logo mr-1">
          <b-img
            id="logo"
            :src="style ? style.reducedHeaderImage : ''"
            alt="logo"
            fluid
            class="rounded"
          />
        </span>
        <h1 class="text-dark h1 mb-0">
          {{ $t('Expedição') }}
        </h1>
        <br>
      </div>
      <div
        v-if="filter.storeId"
        class="d-flex align-items-center mb-2"
      >
        <span>Drive</span>
        <FormulateInput
          id="pickup-point-switch"
          v-model="filter.pickupPoint"
          type="switch"
          :label="null"
          class="ml-2 mt-1"
          @change="getData"
        />
      </div>
      <div v-if="filter.storeId">
        <b-avatar
          size="40"
          icon="wifi"
          variant="light-primary"
          badge
          class="badge-minimal ml-1"
          :badge-variant="preparationWebSocketConnected ? 'success' : 'danger'"
          :title="$t('Indica o status da conexão com a atualização dos pedidos de preparação')"
        />
        &nbsp;
        <b-button
          variant="link-primary"
          :title="$t('Atualizar lista de pedidos')"
          @click="getData"
        >
          <b-icon
            icon="arrow-clockwise"
            font-scale="1"
          />
        </b-button>
        <b-button
          variant="link-primary"
          :title="$t('Testar som')"
          @click="testSound"
        >
          <b-icon
            icon="volume-up"
            font-scale="1"
          />
        </b-button>
      </div>
      <div
        v-if="filter.storeId && isElectron"
        class="d-flex align-items-center mb-2"
      >
        <span>Imprimir pedido ao Iniciar Preparo</span>
        <FormulateInput
          id="print-on-prepare-switch-global"
          v-model="printOnPrepareGlobal"
          type="switch"
          :label="null"
          class="ml-2 mt-1"
        />
      </div>
      <div style="min-width: 300px">
        <e-store-combo
          v-model="filter.storeId"
          :disabled="!ready"
          :required="false"
          :show-label="false"
          :clearable="true"
          :placeholder="ready ? $t('Selecione') : $t('Aguarde...')"
          @input="getData"
        />
      </div>
    </header>
    <div
      v-if="!filter.storeId"
      class="text-center"
    >
      <span class="h2"> {{ needSearchMessage }}</span>
    </div>
    <div v-else>
      <div class="kanban-board d-flex">
        <div class="kanban-column flex-fill mr-25">
          <h1 class="text-center kanban-pending">
            Aguardando
          </h1>
          <preparation-order-card
            v-for="order in aguardandoOrders"
            :key="order.id"
            :order="order"
            :print-on-prepare="printOnPrepareGlobal"
            @update-status="handleUpdateStatus"
          />
        </div>
        <div class="kanban-column flex-fill mr-25">
          <h1 class="text-center kanban-preparing">
            Preparando
          </h1>
          <preparation-order-card
            v-for="order in preparandoOrders"
            :key="order.id"
            :order="order"
            @update-status="handleUpdateStatus"
          />
        </div>
        <div class="kanban-column flex-fill">
          <h1 class="text-center kanban-completed">
            Pronto
          </h1>
          <preparation-order-card
            v-for="order in prontoOrders"
            :key="order.id"
            :order="order"
            @update-status="handleUpdateStatus"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { BImg, BIcon, BButton, BAvatar } from 'bootstrap-vue'
import { app } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import { EStoreCombo } from '@/views/components'
import isElectron from 'is-electron'
import PreparationOrderCard from './components/PreparationOrderCard.vue'
// eslint-disable-next-line global-require
const alarmSound = new Audio(require('@/assets/audio/echope_alert.mp3'))
// eslint-disable-next-line global-require
const alarmSoundDrive = new Audio(require('@/assets/audio/echope_pedido_drive.mp3'))

export default {
  components: {
    BImg,
    BIcon,
    BButton,
    EStoreCombo,
    PreparationOrderCard,
    BAvatar,
  },
  mixins: [app],
  data() {
    return {
      fetching: false,
      orderStatus: {},
      printOnPrepareGlobal: isElectron(),
      now: Date.now(),
      nowIntervalId: null,
      wsReconnectIntervalId: null, // Intervalo para reconexão automática
    }
  },
  computed: {
    ...mapGetters('pages/sale/preparationDashboard', [
      'orders',
      'ready',
      'preparationWebSocketConnected',
    ]),
    ...mapState('pages/sale/preparationDashboard', ['filter', 'lastFetch']),
    aguardandoOrders() {
      return this.orders.filter(o => o.preparationStatus === 'Pending')
    },
    preparandoOrders() {
      return this.orders.filter(o => o.preparationStatus === 'Preparing')
    },
    prontoOrders() {
      // Exibe apenas pedidos concluídos nos últimos 5 minutos, ordenados por preparationStatusDate decrescente
      const {now} = this
      const fiveMinutes = 5 * 60 * 1000
      return this.orders
        .filter(o => {
          if (o.preparationStatus !== 'Done') return false
          const dateStr = o.preparationStatusDate || o.PreparationStatusDate
          if (!dateStr) return false
          const date = new Date(dateStr)
          return now - date.getTime() <= fiveMinutes
        })
        .slice()
        .sort((a, b) => {
          const aDate = a.preparationStatusDate || a.PreparationStatusDate
          const bDate = b.preparationStatusDate || b.PreparationStatusDate
          const dateA = aDate ? new Date(aDate) : new Date(0)
          const dateB = bDate ? new Date(bDate) : new Date(0)
          return dateB - dateA
        })
    },
    needSearchMessage() {
      return this.$t('Selecione a loja para listar os pedidos')
    },

    isElectron() {
      return isElectron()
    },
  },
  watch: {
    orders(val) {
      if (this.fetching) {
        this.previousCount = val?.length
        val.forEach(order => {
          this.orderStatus[order.id] = 'notified'
        })
      } else {
        // eslint-disable-next-line no-lonely-if
        if (this.previousCount < val.length) {
          const newOrders = val.filter(order => Object.keys(this.orderStatus).indexOf(String(order.id)) < 0)
          if (newOrders.some(order => order.preparationStatus === 'Pending')) {
            this.playSound()
          }
          newOrders.forEach(order => {
            this.orderStatus[order.id] = 'new'
            setInterval(() => {
              this.orderStatus[order.id] = 'notified'
            }, 10000)
          })
        }
        this.previousCount = val.length
      }
    },
    preparationWebSocketConnected(val) {
      // Se desconectou, inicia o intervalo de reconexão
      if (!val && !this.wsReconnectIntervalId) {
        this.wsReconnectIntervalId = setInterval(() => {
          this.getData()
        }, 60000)
      }
      // Se reconectou, limpa o intervalo
      if (val && this.wsReconnectIntervalId) {
        clearInterval(this.wsReconnectIntervalId)
        this.wsReconnectIntervalId = null
      }
    },
    async ready(val) {
      if (val) {
        const identify = isElectron() ? await window.electronAPI.system.identifyPayboxRead() : null
        const payBoxPdvFilterStoreId = this.pdvGlobalFilters?.payBox?.store?.id || identify?.storeId
        if (payBoxPdvFilterStoreId && !this.filter.storeId) {
          console.debug(
            'auto-select store config to PDV current payBoxPdvFilterStoreId',
            payBoxPdvFilterStoreId
          )
          this.setFilter({
            ...this.filter,
            storeId: payBoxPdvFilterStoreId.toString(),
          })
          await this.getData()
        }
      }
    },
  },
  async mounted() {
    await this.fetchPreparationWebSocketConfig()
    if (!this.preparationWebSocketConnected) {
      await this.connectToPreparationWebSocket()
    }
    await this.joinPreparationChannel()
    this.nowIntervalId = setInterval(() => {
      this.now = Date.now()
    }, 30000)
  },
  methods: {
    ...mapActions('pages/sale/preparationDashboard', [
      'fetchPreparationOrders',
      'fetchPreparationWebSocketConfig',
      'connectToPreparationWebSocket',
      'joinPreparationChannel',
      'setFilter',
      'updatePreparationStatus',
    ]),
    playSound() {
      if (this.filter.pickupPoint) {
        alarmSoundDrive.play()
      } else {
        alarmSound.play()
      }
    },
    async getData() {
      try {
        this.fetching = true
        await this.fetchPreparationOrders()
        await this.joinPreparationChannel()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },
    testSound() {
      this.playSound()
    },
    async handleUpdateStatus({ saleId, newStatus, print }) {
      try {
        await this.updatePreparationStatus({ saleId, newStatus, print })
      } catch (error) {
        this.showError({ error })
      }
    },
    beforeDestroy() {
      if (this.nowIntervalId) {
        clearInterval(this.nowIntervalId)
      }
      if (this.wsReconnectIntervalId) {
        clearInterval(this.wsReconnectIntervalId)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.brand-logo {
  box-sizing: border-box;
  width: 35px;
}
.kanban-pending {
    background-color: #b8c2cc;
}
.kanban-preparing {
    background-color: #ffc107;
}
.kanban-completed {
    background-color: #4caf50;
}
.kanban-board {
  min-height: 60vh;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  display: flex;
}
.kanban-column {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 5px;
  flex: 0 0 33.3333%;
  min-width: 0;
  max-width: 33.3333%;
  margin-right: 1rem;
  box-sizing: border-box;
}
.kanban-column:last-child {
  margin-right: 0;
}
</style>
